import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api.js';
import { handleApiError } from '../utils/api.js';
import { createLogger } from '../utils/logger.js';

const log = createLogger('Plans');
import {
    Button,
    Card,
    Text,
    Heading,
    Container,
    Alert,
    Badge,
    Spinner,
    MetricGrid
} from '../components/ui/index.jsx';
import Navigation from '../components/Navigation.jsx';
import Footer from '../components/Footer.jsx';

// Paddle client token - environment variable for sandbox/production
const PADDLE_CLIENT_TOKEN = import.meta.env.VITE_PADDLE_CLIENT_TOKEN;
const PADDLE_ENVIRONMENT = import.meta.env.VITE_PADDLE_ENVIRONMENT || 'sandbox';

import CreditGuide from '../components/CreditGuide.jsx';

export default function Plans() {
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [isYearly, setIsYearly] = useState(false);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const paddleInitialized = useRef(false);

    // Initialize Paddle.js
    useEffect(() => {
        if (paddleInitialized.current || !PADDLE_CLIENT_TOKEN) return;

        const script = document.createElement('script');
        script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
        script.async = true;
        script.onload = () => {
            if (window.Paddle) {
                // Set sandbox environment if not production
                if (PADDLE_ENVIRONMENT === 'sandbox') {
                    window.Paddle.Environment.set('sandbox');
                }
                // Initialize with token only
                window.Paddle.Initialize({
                    token: PADDLE_CLIENT_TOKEN
                });
                paddleInitialized.current = true;
                log.log('Paddle initialized in', PADDLE_ENVIRONMENT, 'mode');
            }
        };
        document.head.appendChild(script);

        return () => {
            // Cleanup if needed
        };
    }, []);

    // Fetch dynamic plans from backend when user is authenticated
    useEffect(() => {
        initializePlans();
    }, [isAuthenticated, user]);

    const initializePlans = async () => {
        try {
            setLoading(true);
            setError(null);

            // Use static plans from pricing config (source of truth)
            // Pass user's actual plan to mark it as current
            setPlans(getDefaultPlans(user?.plan || (isAuthenticated ? 'free' : null)));

        } catch (err) {
            setError('Failed to load pricing plans');
            setPlans(getDefaultPlans()); // Always show something
        } finally {
            setLoading(false);
        }
    };

    // Transform backend plan data to component format (3-tier system)
    const transformBackendPlans = (availablePlans, currentPlan) => {
        return availablePlans.map(plan => ({
            id: plan.plan_id,
            name: plan.plan_name,
            description: getDescriptionForPlan(plan.plan_id),
            monthlyPrice: plan.price_monthly || 0,
            yearlyPrice: plan.price_yearly || (plan.price_monthly ? plan.price_monthly * 10 : 0), // ~2 months free
            architectures: plan.architectures || getArchitecturesForPlan(plan.plan_id),
            credits: plan.monthly_credits,
            overageRate: plan.overage_rate || getOverageRateForPlan(plan.plan_id),
            popular: plan.plan_id === 'starter', // Mark Starter as popular (best value)
            features: plan.features || getDefaultFeaturesForPlan(plan.plan_id),
            limitations: getLimitationsForPlan(plan.plan_id),
            isCurrentPlan: plan.plan_id === currentPlan?.id
        }));
    };

    // Helper functions for plan transformation (3-tier system)
    const getDescriptionForPlan = (planId) => {
        const descriptions = {
            'free': 'Try The Architect with 1 free architecture per month',
            'starter': 'Perfect for indie developers and small projects',
            'pro': 'For professional developers and teams'
        };
        return descriptions[planId] || 'Flexible pricing plan';
    };

    const getArchitecturesForPlan = (planId) => {
        const architectures = {
            'free': 1,
            'starter': 5,
            'pro': 15
        };
        return architectures[planId] || 1;
    };

    const getOverageRateForPlan = (planId) => {
        const rates = {
            'free': 5.00,
            'starter': 4.00,
            'pro': 3.50
        };
        return rates[planId] || 5.00;
    };

    const getDefaultFeaturesForPlan = (planId) => {
        const features = {
            'free': [
                '1 Architecture / month',
                '8 Credits'
            ],
            'starter': [
                '5 Architectures / month',
                '40 Credits'
            ],
            'pro': [
                '15 Architectures / month',
                '120 Credits'
            ]
        };
        return features[planId] || [];
    };

    const getLimitationsForPlan = (planId) => {
        return [];
    };

    // Static plans data from pricing config
    // Source of truth: functions/config/pricing.js
    const getDefaultPlans = (currentPlanId = null) => [
        {
            id: 'free',
            name: 'Free',
            description: 'Try The Architect with 1 free architecture per month',
            monthlyPrice: 0,
            yearlyPrice: 0,
            architectures: 1,
            credits: '10 credits/month',
            popular: false,
            isCurrentPlan: currentPlanId === 'free',
            features: [
                '1 Architecture / month',
                '10 Credits'
            ],
            limitations: [],
            overageRate: 5.00
        },
        {
            id: 'starter',
            name: 'Starter',
            description: 'Perfect for indie developers and small projects',
            monthlyPrice: 19,
            yearlyPrice: 190,
            architectures: 5,
            credits: '40 credits/month',
            popular: true,
            isCurrentPlan: currentPlanId === 'starter',
            features: [
                '5 Architectures / month',
                '40 Credits'
            ],
            limitations: [],
            overageRate: 4.00
        },
        {
            id: 'pro',
            name: 'Pro',
            description: 'For professional developers and teams',
            monthlyPrice: 49,
            yearlyPrice: 490,
            architectures: 15,
            credits: '120 credits/month',
            popular: false,
            isCurrentPlan: currentPlanId === 'pro',
            features: [
                '15 Architectures / month',
                '120 Credits'
            ],
            limitations: [],
            overageRate: 3.50
        }
    ];

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
                <div className="text-center">
                    <Spinner size="lg" className="mb-4" />
                    <Text variant="secondary">Loading pricing plans...</Text>
                </div>
            </div>
        );
    }

    const handlePlanSelect = async (planId) => {
        // Check authentication FIRST for all plans
        if (!isAuthenticated) {
            window.showToast?.('Please sign in to choose a plan', 'warning');
            navigate('/signin?redirect=/plans');
            return;
        }

        // Handle Free plan selection for signed-in users
        if (planId === 'free') {
            const currentPlan = user?.plan?.toLowerCase();

            if (!currentPlan || currentPlan === 'free') {
                // Already on free plan (or no plan = free)
                window.showToast?.('You are already on the Free plan', 'info');
                return;
            }

            // User has a paid plan - redirect to billing to cancel/downgrade
            window.showToast?.('To downgrade, cancel your subscription in the Dashboard', 'info');
            navigate('/dashboard?tab=billing');
            return;
        }

        // Check if Paddle is loaded
        if (!window.Paddle) {
            window.showToast?.('Payment system loading, please try again...', 'warning');
            return;
        }

        try {
            setCheckoutLoading(true);
            const selectedPlan = plans.find(p => p.id === planId);
            window.showToast?.(`Opening checkout for ${selectedPlan?.name} plan...`, 'info');

            // Call checkout API to get Paddle config
            const response = await fetch(`${api.config.baseURL}/api/v2/billing/checkout`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    planId,
                    billingCycle: isYearly ? 'yearly' : 'monthly'
                })
            });

            const data = await response.json();

            if (data.success && data.priceId) {
                // Open Paddle checkout overlay
                window.Paddle.Checkout.open({
                    items: [{ priceId: data.priceId, quantity: 1 }],
                    customData: data.customData,
                    customer: data.customerEmail ? { email: data.customerEmail } : undefined,
                    settings: {
                        displayMode: 'overlay',
                        theme: 'dark',
                        locale: 'en',
                        successUrl: data.successUrl
                    },
                    eventCallback: (event) => {
                        log.log('Paddle Event:', event.name);

                        if (event.name === 'checkout.completed') {
                            window.showToast?.('Payment successful! Activating your plan...', 'success');
                            // Redirect to dashboard after successful checkout
                            setTimeout(() => {
                                navigate('/dashboard?checkout=success&plan=' + planId);
                            }, 2000);
                        } else if (event.name === 'checkout.closed') {
                            setCheckoutLoading(false);
                        } else if (event.name === 'checkout.error') {
                            window.showToast?.('Checkout error. Please try again.', 'error');
                            setCheckoutLoading(false);
                        }
                    }
                });
            } else {
                throw new Error(data.error || 'Failed to create checkout session');
            }
        } catch (error) {
            log.error('Checkout error:', error);
            window.showToast?.(error.message || 'Failed to start checkout', 'error');
            setCheckoutLoading(false);
        }
    };

    const formatPrice = (plan) => {
        if (plan.monthlyPrice === 0) return 'Free';

        const price = isYearly ? Math.round(plan.yearlyPrice / 12) : plan.monthlyPrice;
        return `$${price}`;
    };

    const getYearlyDiscount = (plan) => {
        if (plan.monthlyPrice === 0) return 0;
        const monthlyTotal = plan.monthlyPrice * 12;
        const yearlyTotal = plan.yearlyPrice;
        return Math.round(((monthlyTotal - yearlyTotal) / monthlyTotal) * 100);
    };

    // Helper functions for comparison table
    const getProjectLimit = (plan) => {
        const features = plan.features || [];
        const maxProjectFeature = features.find(f => f.includes('Max') && f.includes('project'));
        if (maxProjectFeature) {
            if (maxProjectFeature.includes('Unlimited')) return 'Unlimited';
            const match = maxProjectFeature.match(/\d+/);
            return match ? match[0] : 'N/A';
        }

        // Fallback based on plan type
        if (plan.id === 'free') return '5';
        if (plan.id === 'pro') return '50';
        return 'Unlimited';
    };

    const getPlatformSupport = (plan) => {
        const features = plan.features || [];
        const platformFeature = features.find(f => f.includes('platform'));
        if (platformFeature) {
            if (platformFeature.includes('All')) return 'All';
            if (platformFeature.includes('Cursor') && platformFeature.includes('Base44')) return 'Basic';
        }

        // Fallback based on plan type
        return plan.id === 'free' ? 'Basic' : 'All';
    };

    const getCreditRollover = (plan) => {
        const features = plan.features || [];
        const rolloverFeature = features.find(f => f.includes('rollover'));
        return rolloverFeature ? '✅' : '❌';
    };

    const getSupportLevel = (plan) => {
        const features = plan.features || [];
        const supportFeature = features.find(f => f.includes('support'));
        if (supportFeature) {
            if (supportFeature.includes('Community')) return 'Community';
            if (supportFeature.includes('Email')) return 'Email';
            if (supportFeature.includes('Priority')) return 'Priority';
            if (supportFeature.includes('Dedicated')) return 'Dedicated';
        }

        // Fallback based on plan type
        const supportMap = {
            free: 'Community',
            pro: 'Email',
            business: 'Priority',
            enterprise: 'Dedicated'
        };
        return supportMap[plan.id] || 'Community';
    };

    return (
        <div className="min-h-screen text-white selection:bg-[#7B61FF] selection:text-white relative overflow-hidden">
            {/* Aurora Background */}
            <div className="fixed inset-0 aurora-bg z-0 pointer-events-none"></div>

            {/* Ambient Orbs */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-[#7B61FF]/20 rounded-full blur-[100px] animate-pulse-slow"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-[#9F7AEA]/20 rounded-full blur-[100px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
            </div>

            {/* Content Wrapper */}
            <div className="relative z-10 flex flex-col min-h-screen">
                <Navigation />

                <main className="flex-1">
                    <Container size="7xl">
                        {/* Error Banner - only for initialization errors */}
                        {error && (
                            <Alert variant="error" className="mb-8 border-red-500/30 bg-red-500/10">
                                <div className="flex items-center justify-between">
                                    <div className="flex">
                                        <div className="text-red-400 mr-3">❌</div>
                                        <div>
                                            <Text variant="primary" size="sm" weight="medium">
                                                Failed to load pricing plans
                                            </Text>
                                            <Text variant="secondary" size="sm">{error}</Text>
                                        </div>
                                    </div>
                                    <Button onClick={initializePlans} variant="danger" size="sm">
                                        Retry
                                    </Button>
                                </div>
                            </Alert>
                        )}

                        {/* Hero Section */}
                        <div className="py-16 md:py-24 text-center px-4">
                            <Heading level={1} className="mb-6 bg-clip-text text-transparent bg-gradient-to-b from-white to-white/50">
                                Choose Your Perfect Plan
                            </Heading>
                            <Text variant="secondary" size="xl" className="max-w-3xl mx-auto mb-8">
                                From free trials to enterprise solutions, we have the right plan for your AI-powered app development needs.
                            </Text>
                            <div className="w-24 h-1 bg-[#7B61FF] mx-auto rounded-full shadow-[0_0_10px_#7B61FF] mb-12"></div>

                            {/* Pricing Toggle */}
                            <div className="flex items-center justify-center space-x-4 mb-12">
                                <span className={`text-lg font-medium cursor-pointer transition-colors ${!isYearly ? 'text-white' : 'text-gray-500'}`} onClick={() => setIsYearly(false)}>
                                    Monthly
                                </span>
                                <button
                                    onClick={() => setIsYearly(!isYearly)}
                                    className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors border border-white/10 ${isYearly ? 'bg-[#7B61FF]' : 'bg-[#2A2D36]'
                                        }`}
                                >
                                    <span
                                        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${isYearly ? 'translate-x-7' : 'translate-x-1'
                                            }`}
                                    />
                                </button>
                                <span className={`text-lg font-medium cursor-pointer transition-colors ${isYearly ? 'text-white' : 'text-gray-500'}`} onClick={() => setIsYearly(true)}>
                                    Yearly
                                </span>
                                <Badge variant="info" className="ml-2 bg-[#7B61FF]/20 text-[#BA87FF] border border-[#7B61FF]/40">
                                    Save ~17%
                                </Badge>
                            </div>
                        </div>


                        {/* Pricing Cards - manual grid for perfect centering */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                            {plans.map((plan) => {
                                // ============================================================
                                // 🚧 BETA LAUNCH: Grey out paid plans
                                // To enable paid plans: change `true` to `false` below
                                // ============================================================
                                const PAID_PLANS_DISABLED = true; // <-- TOGGLE THIS TO ENABLE PAID PLANS
                                const isComingSoon = PAID_PLANS_DISABLED && plan.id !== 'free';

                                return (
                                <div
                                    key={plan.id}
                                    className={`relative rounded-2xl transition-all duration-300 backdrop-blur-md bg-white/5 flex flex-col ${
                                        isComingSoon
                                            ? 'opacity-60 grayscale-[30%] border border-white/10'
                                            : plan.popular
                                                ? 'border border-[#7B61FF] shadow-[0_0_30px_rgba(123,97,255,0.15)] scale-105 z-10'
                                                : 'border border-white/10 hover:border-white/20 hover:bg-white/10'
                                        }`}
                                >
                                    {/* Coming Soon Badge for paid plans */}
                                    {isComingSoon && (
                                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-full text-center z-20">
                                            <span className="bg-gray-600 text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg border border-gray-500/30 uppercase tracking-wider">
                                                Coming Soon
                                            </span>
                                        </div>
                                    )}
                                    {plan.popular && !isComingSoon && (
                                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-full text-center">
                                            <span className="bg-[#7B61FF] text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg border border-[#BA87FF]/30 uppercase tracking-wider">
                                                Most Popular
                                            </span>
                                        </div>
                                    )}

                                    <div className="p-8 flex-1 flex flex-col">
                                        <div className="text-center mb-8">
                                            <Heading level={3} className="mb-2 text-white">{plan.name}</Heading>
                                            <Text variant="secondary" className="mb-4 h-10 flex items-center justify-center">{plan.description}</Text>

                                            <div className="mb-4 flex items-baseline justify-center">
                                                <Heading level={2} className="text-5xl font-bold text-white">
                                                    {formatPrice(plan)}
                                                </Heading>
                                                {plan.monthlyPrice !== null && plan.monthlyPrice > 0 && (
                                                    <Text variant="secondary" className="ml-2">
                                                        /mo
                                                    </Text>
                                                )}
                                            </div>

                                            {isYearly && getYearlyDiscount(plan) > 0 && (
                                                <Text variant="accent" size="sm" weight="medium" className="mb-2 block">
                                                    Save {getYearlyDiscount(plan)}% with yearly billing
                                                </Text>
                                            )}

                                            <div className="mt-6 py-3 px-4 bg-[#7B61FF]/10 rounded-xl border border-[#7B61FF]/20">
                                                <div className="text-lg font-semibold text-[#BA87FF]">
                                                    {plan.architectures ? (
                                                        <>
                                                            <span className="text-2xl text-white">{plan.architectures}</span> architecture{plan.architectures > 1 ? 's' : ''}/month
                                                        </>
                                                    ) : (
                                                        typeof plan.credits === 'string' ? plan.credits : `${plan.credits.toLocaleString()} credits/month`
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="space-y-4 mb-8 flex-1">
                                            {plan.features.map((feature, index) => (
                                                <div key={index} className="flex items-start">
                                                    <div className="flex-shrink-0 mt-0.5">
                                                        <svg className="w-5 h-5 text-[#7B61FF]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    </div>
                                                    <Text className="text-gray-300 ml-3 text-sm">{feature}</Text>
                                                </div>
                                            ))}

                                            {plan.limitations.map((limitation, index) => (
                                                <div key={index} className="flex items-start opacity-50">
                                                    <div className="flex-shrink-0 mt-0.5">
                                                        <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </div>
                                                    <Text className="text-gray-500 ml-3 text-sm line-through">{limitation}</Text>
                                                </div>
                                            ))}
                                        </div>

                                        <Button
                                            onClick={() => !plan.isCurrentPlan && !checkoutLoading && !isComingSoon && handlePlanSelect(plan.id)}
                                            disabled={plan.isCurrentPlan || checkoutLoading || isComingSoon}
                                            className={`w-full py-4 rounded-xl font-bold transition-all shadow-lg ${
                                                isComingSoon
                                                    ? 'bg-gray-600/30 text-gray-400 border border-gray-500/30 cursor-not-allowed'
                                                    : plan.isCurrentPlan
                                                        ? 'bg-[#7B61FF]/20 text-white border border-[#7B61FF]/50 cursor-default shadow-[0_0_20px_rgba(123,97,255,0.1)]'
                                                        : checkoutLoading
                                                            ? 'bg-gray-500/20 text-gray-400 cursor-wait'
                                                            : plan.popular
                                                                ? 'bg-[#7B61FF] hover:bg-[#6A51E6] text-white shadow-[#7B61FF]/25 hover:shadow-[#7B61FF]/40'
                                                                : plan.id === 'free'
                                                                    ? 'bg-white/10 hover:bg-white/20 text-white border border-white/10'
                                                                    : 'bg-transparent border border-[#7B61FF]/50 text-[#BA87FF] hover:bg-[#7B61FF]/10'
                                                }`}
                                        >
                                            {isComingSoon
                                                ? 'Coming Soon'
                                                : plan.isCurrentPlan
                                                    ? 'Current Plan'
                                                    : checkoutLoading
                                                        ? 'Redirecting...'
                                                        : plan.id === 'free'
                                                            ? 'Get Started Free'
                                                            : 'Choose ' + plan.name}
                                        </Button>
                                    </div>
                                </div>
                            );
                            })}
                        </div>

                        {/* Credit Infographic Section */}
                        <div className="mt-20 mb-16 max-w-6xl mx-auto px-4">
                            <div className="text-center mb-10">
                                <Heading level={2} className="mb-4 text-white">
                                    Understanding Your Credits
                                </Heading>
                                <Text variant="secondary" size="lg">
                                    A quick guide to how credits, subscriptions, and billing work.
                                </Text>
                            </div>

                            <CreditGuide />
                        </div>

                    </Container>
                </main>

                <Footer />
            </div>
        </div>
    );
}